import { useCallback } from 'react';
import { isNotNil } from '~/utilities/type-guards';
import { useMediaLimitQuery } from './__gql/useMediaLimit.gql.gen';

interface MediaLimitInfo {
  mediaCount: number;
  inflexibleMediaLimit: number | null;
  canUploadMoreMedia: boolean;
  shouldBlockUpload: (files: File[], ignoreLimit: boolean) => boolean;
  shouldBlockAddMedia: ({ count }: { count: number }) => boolean;
  remainingCount: number | null;
}

export const useMediaLimit = (): MediaLimitInfo => {
  const { data } = useMediaLimitQuery();
  const mediaCount = data?.currentAccount?.mediaCount ?? 0;
  const inflexibleMediaLimit = data?.currentAccount?.inflexibleMediaLimit ?? null;

  const canUploadMoreMedia = inflexibleMediaLimit == null || mediaCount < inflexibleMediaLimit;

  const shouldBlockAddMedia = useCallback(
    ({ count }) => {
      if (inflexibleMediaLimit == null) {
        return false;
      }
      return mediaCount + count > inflexibleMediaLimit;
    },
    [inflexibleMediaLimit, mediaCount],
  );

  const shouldBlockUpload = useCallback(
    (files: File[], ignoreLimit: boolean) => {
      if (ignoreLimit) {
        return false;
      }
      return shouldBlockAddMedia({ count: files.length });
    },
    [shouldBlockAddMedia],
  );

  const remainingCount = isNotNil(inflexibleMediaLimit)
    ? Math.max(0, inflexibleMediaLimit - mediaCount)
    : null;

  return {
    mediaCount,
    inflexibleMediaLimit,
    canUploadMoreMedia,
    shouldBlockUpload,
    shouldBlockAddMedia,
    remainingCount,
  };
};
